import React, { useEffect } from 'react';
import {
	ContentEditableType,
	DATA_CONTENT_EDITABLE_TYPE,
	DATA_CONTENT_EDITABLE_IDX,
	FIXED_CONTAINER_ID,
	getIsApplyingFormat,
	getShadowRoot,
	RICH_TEXT_BAR_ID,
} from '@bay-email/editor';
import { useField, useForm } from 'react-final-form';
import { usesListingMergeTags } from '../../../AttributePanel/components/blocks/shared/listingAttachment';

export interface InlineTextProps {
	idx: string;
	children?: React.ReactNode;
	onChange: (content: string) => void;
}

export function InlineText({ idx, onChange, children }: InlineTextProps) {
	const {
		mutators: { setFieldTouched },
	} = useForm();

	// Register field for attribute panel access - RichTextField only registers when actively editing
	// This ensures the field is always available for the attribute panel
	useField(idx);

	useEffect(() => {
		const shadowRoot = getShadowRoot();
		let previousHasListingMergeTags: boolean | null = null;

		const hasListingMergeTags = (contentEditableElement: Element) => {
			if (!(contentEditableElement instanceof HTMLElement)) return false;

			const hasListingBadge = Boolean(
				contentEditableElement.querySelector(
					'.bay-email-merge-tag[data-tag-key^="listing."]',
				),
			);
			if (hasListingBadge) return true;

			return usesListingMergeTags(contentEditableElement.innerHTML || '');
		};

		const isToolbarInteractionTarget = (target: EventTarget | null) => {
			if (!(target instanceof Element)) return false;

			const fixedContainer = document.getElementById(FIXED_CONTAINER_ID);
			const richTextBar = shadowRoot.getElementById(RICH_TEXT_BAR_ID);
			const toolbarPlaceholder = document.getElementById('rich-text-toolbar-placeholder');
			const floatingToolbar = document.getElementById(RICH_TEXT_BAR_ID);

			return Boolean(
				fixedContainer?.contains(target) ||
				floatingToolbar?.contains(target) ||
				richTextBar?.contains(target) ||
				toolbarPlaceholder?.contains(target) ||
				target.closest('.bay-email-extensions-emailToolItem') ||
				target.closest('.bay-email-rich-text-color-panel') ||
				target.closest('.sketch-picker') ||
				target.closest('.arco-trigger-popup') ||
				target.closest('.arco-dropdown') ||
				target.closest('.bay-color-picker__popover') ||
				target.closest('.bay-color-picker'),
			);
		};

		// TiptapEditable owns paste, input, blur and keys for its own element.
		const isTiptapTarget = (target: EventTarget | null) =>
			target instanceof HTMLElement && target.dataset.editor === 'tiptap';

		const onPaste = (e: ClipboardEvent) => {
			if (isTiptapTarget(e.target)) return;
			if (!(e.target instanceof Element) || !e.target.getAttribute('contenteditable'))
				return;

			// CRITICAL: Only handle paste for THIS specific idx
			const targetIdx = e.target.getAttribute(DATA_CONTENT_EDITABLE_IDX);
			if (targetIdx !== idx) return;

			e.preventDefault();

			const text = e.clipboardData?.getData('text/plain') || '';
			document.execCommand('insertHTML', false, text);
			const contentEditableType = e.target.getAttribute(DATA_CONTENT_EDITABLE_TYPE);
			if (contentEditableType === ContentEditableType.RichText) {
				onChange(e.target.innerHTML || '');
			} else if (contentEditableType === ContentEditableType.Text) {
				onChange(e.target.textContent?.trim() || '');
			}
		};

		const onInput = (e: Event) => {
			if (isTiptapTarget(e.target)) return;
			// Skip onChange if we're currently applying formatting from toolbar
			// This prevents the canvas from re-rendering and losing selection
			if (getIsApplyingFormat()) {
				return;
			}

			if (!(e.target instanceof Element) || !e.target.getAttribute('contenteditable')) {
				return;
			}

			const targetIdx = e.target.getAttribute(DATA_CONTENT_EDITABLE_IDX);
			if (targetIdx !== idx) return;

			const nextHasListingMergeTags = hasListingMergeTags(e.target);

			if (previousHasListingMergeTags === null) {
				previousHasListingMergeTags = nextHasListingMergeTags;
				return;
			}

			if (previousHasListingMergeTags === nextHasListingMergeTags) {
				return;
			}

			previousHasListingMergeTags = nextHasListingMergeTags;

			const contentEditableType = e.target.getAttribute(DATA_CONTENT_EDITABLE_TYPE);
			const newContent =
				contentEditableType === ContentEditableType.RichText
					? e.target.innerHTML || ''
					: e.target.textContent?.trim() || '';

			onChange(newContent);
			setFieldTouched(idx, true);
		};

		const onBlur = (e: Event) => {
			if (isTiptapTarget(e.target)) return;
			// CRITICAL: Only handle blur for THIS specific idx
			// Call onChange when user is done editing (clicks away)
			if (e.target instanceof Element && e.target.getAttribute('contenteditable')) {
				const targetIdx = e.target.getAttribute(DATA_CONTENT_EDITABLE_IDX);
				if (targetIdx !== idx) return;
				previousHasListingMergeTags = null;

				window.setTimeout(() => {
					const nextFocusTarget =
						(e as FocusEvent).relatedTarget ||
						shadowRoot.activeElement ||
						document.activeElement;

					if (isToolbarInteractionTarget(nextFocusTarget)) {
						return;
					}

					const contentEditableType = e.target.getAttribute(DATA_CONTENT_EDITABLE_TYPE);
					const newContent =
						contentEditableType === ContentEditableType.RichText
							? e.target.innerHTML || ''
							: e.target.textContent?.trim() || '';

					onChange(newContent);

					// Mark field as touched so form knows it changed
					setFieldTouched(idx, true);
				}, 0);
			}
		};

		const isMergeTagBadge = (node: Node | null): node is HTMLElement =>
			node instanceof HTMLElement &&
			node.classList.contains('bay-email-merge-tag');

		// Skip over empty/whitespace-only text nodes so a badge separated from the
		// caret by an empty text node is still found.
		const skipEmptyText = (node: Node | null, dir: 'back' | 'forward') => {
			let n = node;
			while (n && n.nodeType === Node.TEXT_NODE && !(n.textContent || '').trim()) {
				n = dir === 'back' ? n.previousSibling : n.nextSibling;
			}
			return n;
		};

		// Find a merge-tag badge sitting immediately on the side of a collapsed caret
		// that the key would delete toward (Backspace → before, Delete → after).
		const getCaretAdjacentBadge = (dir: 'back' | 'forward') => {
			const selection = (shadowRoot as any)?.getSelection?.();
			if (!selection || selection.rangeCount === 0) return null;
			const range = selection.getRangeAt(0);
			if (!range.collapsed) return null;

			const { startContainer, startOffset } = range;
			let candidate: Node | null = null;

			if (startContainer.nodeType === Node.ELEMENT_NODE) {
				const kids = (startContainer as Element).childNodes;
				candidate = dir === 'back' ? kids[startOffset - 1] : kids[startOffset];
			} else if (startContainer.nodeType === Node.TEXT_NODE) {
				const len = startContainer.textContent?.length ?? 0;
				if (dir === 'back' && startOffset === 0) {
					candidate = startContainer.previousSibling;
				} else if (dir === 'forward' && startOffset === len) {
					candidate = startContainer.nextSibling;
				}
			}

			candidate = skipEmptyText(candidate, dir);
			return isMergeTagBadge(candidate) ? candidate : null;
		};

		const onKeyDown = (e: Event) => {
			if (isTiptapTarget(e.target)) return;
			const ke = e as KeyboardEvent;
			if (ke.key !== 'Backspace' && ke.key !== 'Delete') return;
			const dir = ke.key === 'Backspace' ? 'back' : 'forward';

			// A merge-tag badge is a focusable <input>, so a key pressed while it has
			// focus is swallowed and the badge never gets removed. And because it is a
			// replaced element, native Backspace/Delete from an adjacent caret is also
			// unreliable. Either way the tag — and the Listing Settings panel that keys
			// off it — lingers after the user "deletes" it, so remove it ourselves.
			const active = shadowRoot.activeElement;
			const badge: HTMLElement | null = isMergeTagBadge(
				e.target instanceof Node ? e.target : null,
			)
				? (e.target as HTMLElement)
				: isMergeTagBadge(active)
					? active
					: getCaretAdjacentBadge(dir);
			if (!badge) return;

			const editable = badge.closest('[contenteditable="true"]') as HTMLElement | null;
			if (!editable) return;
			if (editable.getAttribute(DATA_CONTENT_EDITABLE_IDX) !== idx) return;

			ke.preventDefault();

			// Remember where the badge was so the caret stays put after removal.
			const parent = badge.parentNode;
			const caretIndex = parent
				? Array.prototype.indexOf.call(parent.childNodes, badge)
				: -1;
			badge.remove();
			editable.focus();
			if (parent && caretIndex >= 0) {
				try {
					const caret = document.createRange();
					caret.setStart(parent, Math.min(caretIndex, parent.childNodes.length));
					caret.collapse(true);
					const selection = (shadowRoot as any)?.getSelection?.();
					selection?.removeAllRanges?.();
					selection?.addRange?.(caret);
				} catch {
					/* caret restore is best-effort */
				}
			}

			const contentEditableType = editable.getAttribute(DATA_CONTENT_EDITABLE_TYPE);
			const newContent =
				contentEditableType === ContentEditableType.RichText
					? editable.innerHTML || ''
					: editable.textContent?.trim() || '';

			previousHasListingMergeTags = hasListingMergeTags(editable);
			onChange(newContent);
			setFieldTouched(idx, true);
		};

		shadowRoot.addEventListener('paste', onPaste as any, true);
		shadowRoot.addEventListener('input', onInput);
		shadowRoot.addEventListener('blur', onBlur, true);
		shadowRoot.addEventListener('keydown', onKeyDown, true);

		return () => {
			shadowRoot.removeEventListener('paste', onPaste as any, true);
			shadowRoot.removeEventListener('input', onInput);
			shadowRoot.removeEventListener('blur', onBlur, true);
			shadowRoot.removeEventListener('keydown', onKeyDown, true);
		};
	}, [idx, onChange, setFieldTouched]);

	return <>{children}</>;
}
